import React from 'react';
import { motion } from 'motion/react';
import {
  Tractor,
  MapPin,
  Layers,
  Calendar,
  ClipboardList,
  Users,
  Wallet,
  PiggyBank,
  TrendingUp,
  BarChart3,
  BookOpen,
  Brain,
  Leaf,
  Building2,
  Store,
  RefreshCw,
  LayoutDashboard,
  ArrowRight,
  Check,
  Globe,
  Smartphone,
  Cloud,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PDFDownloadButton from '../components/PDFDownloadButton';
import '../styles/totalfarm.css';

const featureCategories = [
  {
    category: 'Farm & Field Management',
    icon: Tractor,
    color: 'green',
    features: [
      { name: 'Multi-Farm Support', desc: 'Create and manage multiple farms' },
      { name: 'GPS Location', desc: 'Set precise farm coordinates' },
      { name: 'Field Management', desc: 'Add fields with soil type, irrigation, size' },
      { name: 'Infrastructure Tracking', desc: 'Buildings, equipment, vehicles, wells' },
    ],
  },
  {
    category: 'Crop Management',
    icon: Leaf,
    color: 'lime',
    features: [
      { name: 'Crop Seasons', desc: 'Track planting to harvest cycles' },
      { name: 'Crop Varieties', desc: 'Manage different crop types' },
      { name: 'Crop Calendar', desc: 'Visual calendar by country' },
      { name: 'Crop Diary', desc: 'Daily journal entries for each field' },
    ],
  },
  {
    category: 'Worker Management',
    icon: Users,
    color: 'blue',
    features: [
      { name: 'Worker Profiles', desc: 'Add workers with details' },
      { name: 'Daily Rates', desc: 'Set payment rates per worker' },
      { name: 'Attendance Tracking', desc: 'Track worker attendance' },
      { name: 'Task Assignment', desc: 'Assign tasks to specific workers' },
    ],
  },
  {
    category: 'Payments & Finance',
    icon: Wallet,
    color: 'purple',
    features: [
      { name: 'Worker Payments', desc: 'Record all payments made' },
      { name: 'Payment History', desc: 'Full payment records' },
      { name: 'Mobile Money', desc: 'Support for mobile payments' },
      { name: 'Currency Converter', desc: '18 African currencies' },
    ],
  },
  {
    category: 'Cost & Budget',
    icon: PiggyBank,
    color: 'orange',
    features: [
      { name: 'Expense Categories', desc: 'Seeds, fertilizer, labor, etc.' },
      { name: 'Budget Planning', desc: 'Set budgets per season/field' },
      { name: 'Spending Tracker', desc: 'Track spending vs budget' },
      { name: 'Cost Reports', desc: 'Detailed expense reports' },
    ],
  },
  {
    category: 'Yield & Profits',
    icon: TrendingUp,
    color: 'emerald',
    features: [
      { name: 'Harvest Recording', desc: 'Record quantities harvested' },
      { name: 'Sale Prices', desc: 'Track selling prices' },
      { name: 'Profit Projection', desc: 'Calculate expected profits' },
      { name: 'ROI Analysis', desc: 'Return on investment tracking' },
    ],
  },
];

const aiFeatures = [
  {
    title: 'AI Farm Advisor',
    description: 'Get intelligent farming advice based on your crops, soil conditions, and local climate.',
    icon: Brain,
  },
  {
    title: 'Fertilizer Recommendations',
    description: 'Receive personalized fertilizer advice based on your specific crop and soil type.',
    icon: Leaf,
  },
  {
    title: 'Smart Insights',
    description: 'AI-powered analytics to optimize your farming operations and maximize yields.',
    icon: BarChart3,
  },
];

const additionalFeatures = [
  { icon: ClipboardList, title: 'Task Management', desc: 'Create, assign, and track tasks with due dates' },
  { icon: Calendar, title: 'Crop Calendar', desc: 'Visual farming calendar by country' },
  { icon: BookOpen, title: 'Crop Diary', desc: 'Daily journal for each field' },
  { icon: Building2, title: 'Infrastructure', desc: 'Track buildings, equipment, wells' },
  { icon: Store, title: 'Vendor Marketplace', desc: 'Find agricultural suppliers' },
  { icon: LayoutDashboard, title: 'Dashboard Analytics', desc: 'Overview stats, charts, quick actions' },
];

const TotalFarmPage = () => {
  return (
    <div className="totalfarm-page" id="totalfarm-content">
      <Header />
      
      {/* Hero Section */}
      <section className="totalfarm-hero">
        <div className="container-custom">
          <div className="totalfarm-hero-wrapper">
            <div className="totalfarm-hero-content">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="totalfarm-badge"
              >
                <Tractor size={16} />
                Complete Farm Management Solution
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                TotalFarm<span className="text-[#22c55e]">.Pro</span>
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="totalfarm-subtitle"
              >
                The all-in-one farm management platform that makes life easier for farmers. 
                From crop planning to profit tracking, manage everything in one place.
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="totalfarm-hero-buttons"
              >
                <a href="#contact" className="btn-farm-primary">
                  Start Free Trial <ArrowRight size={16} />
                </a>
                <a href="#features" className="btn-farm-secondary">
                  Explore Features
                </a>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="hero-stats"
              >
                <div className="hero-stat">
                  <span className="stat-value">18+</span>
                  <span className="stat-label">African Currencies</span>
                </div>
                <div className="hero-stat">
                  <span className="stat-value">AI</span>
                  <span className="stat-label">Powered Advisor</span>
                </div>
                <div className="hero-stat">
                  <span className="stat-value">100%</span>
                  <span className="stat-label">Mobile Ready</span>
                </div>
              </motion.div>
            </div>
            
            {/* App Screenshots */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="totalfarm-hero-image"
            >
              <div className="phone-mockup-group">
                <div className="phone-mockup-farm phone-front">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_omnisupport-2/artifacts/x676g1rx_1564b16968a9b385011db1458a6bb147f8cc0a9313798d2137f44a58c0a68494.jpeg"
                    alt="TotalFarm.Pro App Dashboard"
                  />
                </div>
                <div className="phone-mockup-farm phone-back">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_omnisupport-2/artifacts/l47c6nuy_image.png"
                    alt="TotalFarm.Pro App Features"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
        
        <div className="totalfarm-hero-bg">
          <div className="farm-blob-1" />
          <div className="farm-blob-2" />
        </div>
      </section>
      
      {/* Features Grid */}
      <section className="totalfarm-features" id="features">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-farm"
          >
            <span className="section-tag-farm">Complete Feature Set</span>
            <h2>Everything Farmers Need</h2>
            <p>Comprehensive tools to manage every aspect of your farming operations</p>
          </motion.div>
          
          <div className="features-category-grid">
            {featureCategories.map((cat, index) => (
              <motion.div
                key={cat.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className={`feature-category-card ${cat.color}`}
              >
                <div className="category-header">
                  <div className="category-icon">
                    <cat.icon size={24} />
                  </div>
                  <h3>{cat.category}</h3>
                </div>
                <ul className="category-features">
                  {cat.features.map((feature, i) => (
                    <li key={i}>
                      <Check size={16} className="check-icon" />
                      <div>
                        <strong>{feature.name}</strong>
                        <span>{feature.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* AI Features */}
      <section className="totalfarm-ai">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-farm light"
          >
            <span className="section-tag-farm light">AI-Powered</span>
            <h2>Smart Farming with AI</h2>
            <p>Leverage artificial intelligence to make better farming decisions</p>
          </motion.div>
          
          <div className="ai-features-grid">
            {aiFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="ai-feature-card"
              >
                <div className="ai-icon">
                  <feature.icon size={32} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Additional Features */}
      <section className="totalfarm-additional">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="section-header-farm"
          >
            <span className="section-tag-farm">And Much More</span>
            <h2>Additional Features</h2>
            <p>Packed with tools to streamline your farming operations</p>
          </motion.div>
          
          <div className="additional-grid">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                className="additional-item"
              >
                <feature.icon size={24} />
                <h4>{feature.title}</h4>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Why Choose */}
      <section className="totalfarm-why">
        <div className="container-custom">
          <div className="why-content">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="why-text"
            >
              <span className="section-tag-farm">Why TotalFarm.Pro</span>
              <h2>Built for African Farmers</h2>
              <p>
                TotalFarm.Pro is designed specifically for the unique needs of African agriculture. 
                With support for 18 African currencies, mobile money integration, and AI-powered 
                advice tailored to local conditions, we help farmers maximize their productivity 
                and profits.
              </p>
              
              <ul className="why-list">
                <li><Check size={20} /> Works offline - sync when connected</li>
                <li><Check size={20} /> Mobile-first design for field use</li>
                <li><Check size={20} /> Local currency & payment support</li>
                <li><Check size={20} /> Crop calendars by country</li>
                <li><Check size={20} /> Simple, intuitive interface</li>
              </ul>
              
              <a href="#contact" className="btn-farm-primary">
                Get Started Today <ArrowRight size={16} />
              </a>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="why-cards"
            >
              <div className="why-card">
                <Globe size={32} />
                <h4>18 African Currencies</h4>
                <p>Convert between currencies easily</p>
              </div>
              <div className="why-card">
                <Smartphone size={32} />
                <h4>Mobile Money</h4>
                <p>Pay workers via mobile money</p>
              </div>
              <div className="why-card">
                <Cloud size={32} />
                <h4>Cloud Sync</h4>
                <p>Access data anywhere, anytime</p>
              </div>
              <div className="why-card">
                <Brain size={32} />
                <h4>AI Advisor</h4>
                <p>Smart farming recommendations</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="totalfarm-cta" id="contact">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="cta-content-farm"
          >
            <h2>Ready to Transform Your Farm?</h2>
            <p>Join thousands of farmers using TotalFarm.Pro to manage their operations more efficiently.</p>
            <div className="cta-buttons-farm">
              <a href="mailto:sales@voxitconsulting.media" className="btn-farm-primary btn-white">
                Start Free Trial
              </a>
              <a href="mailto:sales@voxitconsulting.media" className="btn-farm-secondary btn-outline-white">
                Contact Sales
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
      <PDFDownloadButton 
        contentId="totalfarm-content" 
        fileName="VoxIT-TotalFarm-Pro" 
        buttonText="Download PDF"
      />
    </div>
  );
};

export default TotalFarmPage;
